import React from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import {SEARCH_TYPE} from '../search-prop-types';
import withConfig from 'config/withConfig';
import IconCheck from '@spothero/icons/check';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import Card from 'common/card/Card';
import SpotListItemRow from './SpotListItemRow';
import SpotListItemRowPlaceholder from './SpotListItemRowPlaceholder';
import {updateSelected} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import {resetFilters} from 'filters/filters-actions';

const renderPlaceholders = (count = 5) => {
    const placeholders = [];
    let i = 0;

    for (i; i < count; i++) {
        placeholders.push(<SpotListItemRowPlaceholder key={i} />);
    }

    return <div className="SpotList-spots">{placeholders}</div>;
};

const renderValueProps = valueProps => {
    return valueProps.map(vp => {
        if (isEmpty(vp)) {
            return null;
        }

        return (
            <div key={vp} className="SpotList-value-prop">
                <IconCheck />
                <p>{vp}</p>
            </div>
        );
    });
};

const SpotList = ({
    isMobile,
    searchType,
    spots,
    numActiveFilters,
    lowPrice,
    lowPriceCurrencySymbol,
    highPrice,
    highPriceCurrencySymbol,
    isMonthly,
    starts,
    ends,
    event,
    siteUrl,
    destination,
    city,
    urlDateFormat,
    showLoadMore,
    showListLoader,
    showLoadingComplete,
    loadNextPage,
    onResetFiltersClick,
    updateSelectedSpot,
    cancelAllSpotRequests,
    mapLoadingIncrement,
    isRebookingReservation,
    isAdmin,
    sortBy,
    selectedSpot,
    valueProp1,
    valueProp2,
    valueProp3,
    isEventListEnabled,
    onToggleEventList,
}) => {
    const {
        monthly_phone_number: monthlyPhoneNumber,
        monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
    } = city;
    const hasDestination = !isEmpty(destination);
    const end = isMonthly
        ? moment(starts, moment.ISO_8601)
              .add(1, 'month')
              .format(urlDateFormat)
        : ends;
    const url =
        searchType === 'event' && !isEmpty(event)
            ? `${siteUrl}/${event.search_url}`
            : hasDestination
            ? `${siteUrl}/${city.slug}/${
                  isMonthly && destination.monthly_relative_url
                      ? destination.monthly_relative_url
                      : destination.relative_url
              }?starts=${starts}&ends=${ends}`
            : `${siteUrl}/${
                  isMonthly && city.monthly_relative_url
                      ? city.monthly_relative_url
                      : city.relative_url
              }?starts=${starts}&ends=${ends}`;
    const valueProps = [valueProp1, valueProp2, valueProp3];
    const hasAtLeastOneValueProp = Boolean(valueProps.filter(Boolean).length);

    return (
        <div
            className="SpotList"
            itemProp="offers"
            itemScope
            itemType="http://schema.org/AggregateOffer"
        >
            {url && <meta itemProp="url" content={url} />}
            {!isMobile &&
                (mapLoadingIncrement > 0 && spots.length < 1) &&
                renderPlaceholders()}
            {spots.length > 0 && (
                <span>
                    {lowPrice && (
                        <meta
                            itemProp="lowPrice"
                            content={`${lowPriceCurrencySymbol}${lowPrice}`}
                        />
                    )}
                    {highPrice && (
                        <meta
                            itemProp="highPrice"
                            content={`${highPriceCurrencySymbol}${highPrice}`}
                        />
                    )}
                    <meta itemProp="validFrom" content={starts} />
                    <meta itemProp="validThrough" content={end} />
                </span>
            )}
            <meta itemProp="offerCount" content={spots.length} />
            <div className="SpotList-spots">
                {isMobile && hasAtLeastOneValueProp && (
                    <Card className="SpotList-value-props">
                        {renderValueProps(valueProps)}
                    </Card>
                )}
                {isMobile && isEventListEnabled && (
                    <Card className="SpotList-event-cta">
                        <p>Going to an event at {destination.title}?</p>
                        <Button color="primary" onClick={onToggleEventList}>
                            View Upcoming Events
                        </Button>
                    </Card>
                )}
                {isMonthly && monthlyPhoneNumber && (
                    <Card className="SpotList-monthly-help">
                        <p>
                            Questions about monthly parking?{' '}
                            {!isMobile && 'Give us a call at '}
                            <a href={`tel:${monthlyPhoneNumber}`}>
                                {monthlyPhoneNumberFriendly}
                            </a>
                            .
                        </p>
                    </Card>
                )}
                {spots
                    .filter(({selectedRate}) => {
                        // ensure spots with either missing selectedRate
                        // or those without the proper selectedRate.rule_type are not offered to users
                        return (
                            selectedRate &&
                            ((isMonthly &&
                                selectedRate.rule_type === 'monthly') ||
                                (!isMonthly &&
                                    selectedRate.rule_type !== 'monthly'))
                        );
                    })
                    .map(spot => (
                        <SpotListItemRow
                            searchType={searchType}
                            key={`${spot.spotId}-${spot.activeMarker}`}
                            spot={{...spot}}
                            isMonthly={isMonthly}
                            isRebookingReservation={isRebookingReservation}
                            isAdmin={isAdmin}
                            updateSelectedSpot={updateSelectedSpot}
                            cancelAllSpotRequests={cancelAllSpotRequests}
                            currentlySelectedSpot={selectedSpot}
                        />
                    ))}
            </div>
            {isMobile &&
                !showLoadingComplete &&
                (!spots || spots.length === 0) &&
                renderPlaceholders()}
            {isMobile && showLoadingComplete && (
                <div className="SpotList-actions">
                    <Alert className="text-center" type="muted">
                        <strong>Still haven&apos;t found a spot?</strong> Try
                        searching a different destination or time above.
                        {numActiveFilters > 0 && (
                            <span>
                                {' '}
                                Or
                                <TextButton onClick={onResetFiltersClick}>
                                    reset filters
                                </TextButton>
                                .
                            </span>
                        )}
                    </Alert>
                </div>
            )}
            {isMobile &&
                showLoadMore &&
                !showLoadingComplete &&
                (spots && spots.length > 0) && (
                    <div className="SpotList-actions">
                        <Button
                            color="primary"
                            loading={showListLoader}
                            onClick={loadNextPage}
                        >
                            {sortBy === 'price'
                                ? 'Load More and Re-Sort'
                                : 'Load More Spots'}
                        </Button>
                    </div>
                )}
        </div>
    );
};

SpotList.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    numActiveFilters: PropTypes.number,
    lowPrice: PropTypes.number,
    lowPriceCurrencySymbol: PropTypes.string,
    highPrice: PropTypes.number,
    highPriceCurrencySymbol: PropTypes.string,
    isMonthly: PropTypes.bool.isRequired,
    showLoadMore: PropTypes.bool,
    showListLoader: PropTypes.bool,
    showLoadingComplete: PropTypes.bool,
    searchType: SEARCH_TYPE.isRequired,
    starts: PropTypes.string,
    ends: PropTypes.string,
    spots: PropTypes.arrayOf(PropTypes.object),
    siteUrl: PropTypes.string,
    urlDateFormat: PropTypes.string,
    loadNextPage: PropTypes.func,
    onResetFiltersClick: PropTypes.func,
    cancelAllSpotRequests: PropTypes.func.isRequired,
    updateSelectedSpot: PropTypes.func.isRequired,
    mapLoadingIncrement: PropTypes.number,
    isRebookingReservation: PropTypes.bool,
    isAdmin: PropTypes.bool,
    sortBy: PropTypes.string.isRequired,
    event: PropTypes.object,
    destination: PropTypes.shape(DESTINATION_DATA).isRequired,
    city: PropTypes.object,
    selectedSpot: PropTypes.object,
    valueProp1: PropTypes.string,
    valueProp2: PropTypes.string,
    valueProp3: PropTypes.string,
    isEventListEnabled: PropTypes.bool,
    onToggleEventList: PropTypes.func,
};

SpotList.defaultProps = {
    lowPriceCurrencySymbol: '$',
    highPriceCurrencySymbol: '$',
};

const mapStateToProps = state => {
    const {
        search: {
            data: {currentIncrement: mapLoadingIncrement, searchType, sortBy},
        },
        searchRequest: {starts, ends, monthly: isMonthly},
        checkout: {isRebookingReservation},
        filters: {
            numActive: numActiveFilters,
            priceLow: lowPrice,
            priceHigh: highPrice,
        },
        user: {
            data: {
                isAdmin,
                experiments: {
                    mw_landing_value_props: {
                        value_prop_1: valueProp1,
                        value_prop_2: valueProp2,
                        value_prop_3: valueProp3,
                    } = {},
                },
            },
        },
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        spot: {selected: selectedSpot},
    } = state;

    return {
        searchType,
        isMonthly,
        starts,
        ends,
        numActiveFilters,
        sortBy,
        lowPrice,
        highPrice,
        event,
        destination,
        city,
        mapLoadingIncrement,
        isRebookingReservation,
        isAdmin,
        selectedSpot,
        valueProp1,
        valueProp2,
        valueProp3,
    };
};

const mapDispatchToProps = {
    onResetFiltersClick: resetFilters,
    cancelAllSpotRequests: cancelSpotRequests,
    updateSelectedSpot: updateSelected,
};

const enhance = compose(
    withConfig(['isMobile', 'siteUrl', 'urlDateFormat']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotList);
